import { Css, DnDGridItemHandle, IconButton, Tag, Tooltip, useDnDGridItem, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { useRef } from "react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BasePlanGroupDimensionsModal } from "src/routes/bids/plan-based-bid-package/components/BasePlanGroupDimensionsModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import {
  groupKey,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";
import { pluralize } from "src/utils";

type BaseGroupCardProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  group: ObjectState<PlanBasedBidPackageBidPlanGroup>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  productOfferings: ConfigureBidGroupsStepQuery["bidPackage"]["productOfferings"];
};

export const BaseGroupCard = observer(function BaseGroupCard(props: BaseGroupCardProps) {
  const { formState, group, dimensionResults, productOfferings } = props;
  const { openModal } = useModal();
  const hasDimensions = group.dimensions.rows.nonEmpty;
  // Pull plan names based on the product offerings in the form state
  const planNames = (formState.productOfferings.value ?? [])
    .map((id) => productOfferings.find((po) => po.id === id)?.name)
    .compact();
  const itemRef = useRef(null);
  const { dragHandleProps, dragItemProps } = useDnDGridItem({ id: groupKey(group.value)!, itemRef });

  function openEditModal() {
    openModal({
      content: (
        <BasePlanGroupDimensionsModal formState={formState} dimensionResults={dimensionResults} editingGroup={group} />
      ),
    });
  }

  function removeGroup() {
    const key = groupKey(group.value);
    const next = (formState.bidPlanGroups.value ?? []).filter((g) => groupKey(g) !== key);
    formState.bidPlanGroups.set(renumberGroups(next));
  }

  return (
    <div css={Css.df.fdc.gap2.bt.bcGray200.pt3.$} ref={itemRef} {...dragItemProps}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.df.aic.gap1.$}>
          {planNames.nonEmpty && (
            <div css={Css.mya.$}>
              <Tag type="info" text={`${planNames.length} ${pluralize(planNames.length, "Product Offering")}`} />
            </div>
          )}
        </div>
        <div css={Css.df.aic.gap1.$}>
          <IconButton icon="pencil" onClick={openEditModal} />
          <IconButton icon="trash" onClick={removeGroup} />
          <DnDGridItemHandle icon="drag" dragHandleProps={dragHandleProps} />
        </div>
      </div>
      {planNames.nonEmpty && (
        <Tooltip title="Adjust which product offerings to include in this bid in step 1.">
          <div>{planNames.join(", ")}</div>
        </Tooltip>
      )}
      {hasDimensions ? (
        <BidPlanDimensionRows group={group} dimensionResults={dimensionResults} />
      ) : (
        <div css={Css.bgGray100.br8.py3.tac.gray700.$}>No price dimensions added.</div>
      )}
    </div>
  );
});
